import React from "react";
import { useSelector } from "react-redux";
import { isPremiumOrPro } from "../../utils/SubUtils";
import LockedFeature from "./LockedFeature";
import moment from "moment";

const getValue = (data, keyPath) => {
  if (!data || !keyPath || typeof keyPath !== "string") return undefined;
  return keyPath.split(".").reduce((acc, key) => (acc !== undefined && acc !== null ? acc[key] : undefined), data);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || num === "") return "-";
  const numeric = Number(num);
  if (Number.isNaN(numeric)) return String(num);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
};

const formatCompactCurrency = (num) => {
  if (num === null || num === undefined || num === "") return "-";
  const numeric = Number(num);
  if (Number.isNaN(numeric)) return String(num);
  if (Math.abs(numeric) >= 1.0e+12) return `$${(numeric / 1.0e+12).toFixed(2)}T`;
  if (Math.abs(numeric) >= 1.0e+9) return `$${(numeric / 1.0e+9).toFixed(2)}B`;
  if (Math.abs(numeric) >= 1.0e+6) return `$${(numeric / 1.0e+6).toFixed(1)}M`;
  return `$${formatNumber(numeric, 2)}`;
};

const formatValue = (value, type, options = {}) => {
  if (value === null || value === undefined || value === "") return "-";
  switch (type) {
    case "currency":
      return formatCompactCurrency(value);
    case "number":
      return formatNumber(value, 2);
    case "percent": {
      const numericRaw = Number(value);
      if (Number.isNaN(numericRaw)) return String(value);
      const numeric = options.fromFraction ? numericRaw * 100 : numericRaw;
      if (Number.isNaN(numeric)) return String(value);
      const sign = numeric > 0 ? "+" : "";
      return `${sign}${numeric.toFixed(2)}%`;
    }
    case "date": {
      const asDate = new Date(value);
      if (Number.isNaN(asDate.getTime())) return String(value);
      return moment(value).format("MMM DD, YYYY");
    }
    default:
      return String(value);
  }
};

const Metrics = ({ data }) => {
  const user = useSelector((state) => state.auth.user);
  const subscription = user?.user_subscription;
  const isPro = isPremiumOrPro(subscription);

  if (!isPro) {
    return (
      <LockedFeature
        title="Metrics Locked"
        description={<span>Upgrade to <strong>Premium or Pro</strong> plan to unlock company metrics and trading activity data.</span>}
      />
    );
  }

  if (!data) return <div className="text-center p-5">No metrics available.</div>;

  const sections = [
    {
      title: "Valuation",
      rows: [
        { label: "Enterprise Value", key: "valuation.enterprise_value", keyLabel: "enterprise_value", type: "currency", highlightPositive: true },
        { label: "Trailing PE", key: "valuation.trailing_pe", keyLabel: "trailing_pe", type: "number", highlightNegative: true },
        { label: "Forward PE", key: "valuation.forward_pe", keyLabel: "forward_pe", type: "number", highlightNegative: true },
        { label: "PEG Ratio", key: "valuation.peg_ratio", keyLabel: "peg_ratio", type: "number", highlightNegative: true },
        { label: "Price to Sales", key: "valuation.price_to_sales_ttm", keyLabel: "price_to_sales_ttm", type: "number" },
        { label: "Price to Book", key: "valuation.price_to_book_mrq", keyLabel: "price_to_book_mrq", type: "number" },
        { label: "Enterprise to Revenue", key: "valuation.enterprise_to_revenue", keyLabel: "enterprise_to_revenue", type: "number" },
        { label: "Enterprise to EBITDA", key: "valuation.enterprise_to_ebitda", keyLabel: "enterprise_to_ebitda", type: "number" }
      ]
    },
    {
      title: "Financial Position",
      rows: [
        { label: "Total Cash (mrq)", key: "financial_position.total_cash_mrq", keyLabel: "total_cash_mrq", type: "currency", highlightPositive: true },
        { label: "Total Debt (mrq)", key: "financial_position.total_debt_mrq", keyLabel: "total_debt_mrq", type: "currency" },
        { label: "Operating Cash Flow (ttm)", key: "financial_position.operating_cash_flow_ttm", keyLabel: "operating_cash_flow_ttm", type: "currency", highlightNegative: true },
        { label: "Current Ratio (mrq)", key: "financial_position.current_ratio_mrq", keyLabel: "current_ratio_mrq", type: "number" },
        { label: "Book Value Per Share", key: "financial_position.book_value_per_share_mrq", keyLabel: "book_value_per_share_mrq", type: "currency" },
        { label: "Quarterly Earnings Growth", key: "financial_position.quarterly_earnings_growth_yoy", keyLabel: "quarterly_earnings_growth_yoy", type: "percent", fromFraction: true, highlightNegative: true },
        { label: "Cash Per Share (mrq)", key: "financial_position.total_cash_per_share_mrq", keyLabel: "total_cash_per_share_mrq", type: "currency" },
        { label: "Quarterly Revenue Growth", key: "financial_position.quarterly_revenue_growth", keyLabel: "quarterly_revenue_growth", type: "percent", fromFraction: true, highlightNegative: true },
        { label: "EBITDA", key: "financial_position.ebitda", keyLabel: "ebitda", type: "currency" },
        { label: "Ex Dividend Date (if any)", key: "dividends_and_splits.ex_dividend_date", keyLabel: "ex_dividend_date", type: "date" },
        { label: "5 Year Avg Dividend Yield", key: "dividends_and_splits.5_year_average_dividend_yield", keyLabel: "5_year_average_dividend_yield", type: "percent" }
      ]
    },
    {
      title: "Trading Activity",
      rows: [
        { label: "Average Volume (10 days)", key: "trading_activity.avg_10_volume", keyLabel: "avg_10_volume", type: "number" },
        { label: "Average Volume (90 days)", key: "trading_activity.avg_90_volume", keyLabel: "avg_90_volume", type: "number" },
        { label: "Shares in Short Position", key: "trading_activity.shares_short", keyLabel: "shares_short", type: "number" },
        { label: "Short % Outstanding", key: "trading_activity.short_percent_of_shares_outstanding", keyLabel: "short_percent_of_shares_outstanding", type: "percent", fromFraction: true },
        { label: "Short Ratio", key: "trading_activity.short_ratio", keyLabel: "short_ratio", type: "number" }
      ]
    }
  ];

  const mostRecentQuarter = getValue(data, "most_recent_quarter");
  const fiscalYearEnds = getValue(data, "fiscal_year_ends");
  const lastUpdated = getValue(data, "last_updated");

  return (
    <div className="mi-metrics mt-4 px-3">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h3 className="mi-metrics-title mb-1">Metrics</h3>
          <p className="mi-metrics-meta mb-0">
            Most Recent Quarter: {formatValue(mostRecentQuarter, "date")}{" "}
            <span className="mx-1">•</span>
            Fiscal Year Ends: {formatValue(fiscalYearEnds, "date")}
          </p>
        </div>
        <div className="mi-metrics-last-updated">
          Last updated: {formatValue(lastUpdated, "date")}
        </div>
      </div>

      <div className="mi-metrics-card">
        <div className="row g-0">
          {(sections || []).map((section, index) => (
            <div className="col-12 col-lg-4" key={section.title}>
              <div className={`mi-metrics-section ${index < sections.length - 1 ? "with-border" : ""}`}>
                <div className="mi-metrics-section-header">{section.title}</div>
                <div className="mi-metrics-section-body">
                  {(section.rows || []).map((row, rowIndex) => {
                    const rawValue = getValue(data, row && row.key);
                    const formattedValue = formatValue(rawValue, (row && row.type) || "number", { fromFraction: row && row.fromFraction });
                    const numericValue = Number(rawValue);
                    const normalized = row && row.fromFraction ? numericValue * 100 : numericValue;
                    const positive = row && row.type === "percent" && !Number.isNaN(normalized) && normalized > 0;
                    const highlightPositive = row && row.highlightPositive && !Number.isNaN(numericValue) && numericValue > 0;
                    const highlightNegative = row && row.highlightNegative && !Number.isNaN(normalized) && normalized < 0;

                    return (
                      <div className="mi-metrics-row" key={(row && row.key) || `${section.title}-${rowIndex}`}>
                        <div className="mi-metrics-row-left">
                          <span className="mi-metrics-label">{(row && row.label) || "-"}</span>
                        </div>
                        <div className={`mi-metrics-value ${positive || highlightPositive ? "positive" : ""} ${highlightNegative ? "negative" : ""}`}>
                          {formattedValue}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Metrics;
